import React, { useContext, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import {
    AddRounded,
    CloseRounded,
    ContactSupportRounded,
    SendRounded,
    SubjectRounded,
    ChatBubbleOutlineRounded,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ErrorContext } from "../../components/GympinPagesProvider";
import { Support_add } from "../../network/api/support.api";

const _SupportNew = ({ open: externalOpen, onClose: externalOnClose, triggerButton = true, onSuccess }) => {
    const theme = useTheme();
    const corporate = useSelector(({ corporate }) => corporate.corporate);
    const error = useContext(ErrorContext);
    const navigate = useNavigate();

    const [internalOpen, setInternalOpen] = useState(false);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const isControlled = externalOpen !== undefined;
    const isModalOpen = isControlled ? externalOpen : internalOpen;

    const handleOpen = () => {
        if (!isControlled) setInternalOpen(true);
    };

    const handleClose = () => {
        if (submitting) return;
        if (isControlled && externalOnClose) {
            externalOnClose();
        } else {
            setInternalOpen(false);
        }
        setTitle('');
        setMessage('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const trimmedTitle = title.trim();
        const trimmedMessage = message.trim();

        if (!trimmedTitle) {
            error.showError({ message: "لطفاً موضوع تیکت را وارد نمایید." });
            return;
        }
        if (trimmedTitle.length > 50) {
            error.showError({ message: "موضوع تیکت نمی‌تواند بیشتر از ۵۰ کاراکتر باشد." });
            return;
        }
        if (!trimmedMessage) {
            error.showError({ message: "لطفاً متن درخواست تیکت را وارد نمایید." });
            return;
        }
        if (trimmedMessage.length > 500) {
            error.showError({ message: "متن تیکت نمی‌تواند بیشتر از ۵۰۰ کاراکتر باشد." });
            return;
        }

        setSubmitting(true);
        try {
            const result = await Support_add({
                Title: trimmedTitle,
                Message: {
                    Status: "AWAITING_EXPERT",
                    Message: trimmedMessage,
                    IsRead: true,
                },
                CorporateId: corporate?.Id,
            });

            handleClose();
            if (onSuccess) onSuccess();

            const newTicketId = result?.data?.Data?.Id;
            if (newTicketId) {
                navigate(`/support/detail/${newTicketId}`);
            }
        } catch (err) {
            const errMsg = err?.response?.data?.Message || "خطا در ثبت تیکت پشتیبانی. لطفاً دوباره تلاش کنید.";
            error.showError({ message: errMsg });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            {triggerButton && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddRounded />}
                    onClick={handleOpen}
                    sx={{
                        borderRadius: 3,
                        px: 3,
                        py: 1,
                        fontWeight: 700,
                        boxShadow: '0 8px 16px rgba(45, 107, 226, 0.25)',
                    }}
                >
                    ثبت تیکت پشتیبانی جدید
                </Button>
            )}

            <Dialog
                open={isModalOpen}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        p: { xs: 1, sm: 2 },
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'primary.50',
                                color: 'primary.main',
                            }}
                        >
                            <ContactSupportRounded sx={{ fontSize: 24 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                ثبت تیکت جدید
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                کارشناسان جیم‌پین در سریع‌ترین زمان پاسخگوی شما خواهند بود
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleClose}
                        disabled={submitting}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        <CloseRounded />
                    </IconButton>
                </DialogTitle>

                <Box component="form" onSubmit={handleSubmit}>
                    <DialogContent sx={{ px: 2, py: 1.5 }}>
                        <Stack spacing={2.5}>
                            <TextField
                                autoFocus
                                fullWidth
                                label="موضوع تیکت"
                                placeholder="مثلاً: مغایرت در شارژ اعتبار پرسنل، درخواست فاکتور ..."
                                variant="outlined"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                disabled={submitting}
                                inputProps={{ maxLength: 50 }}
                                helperText={`${title.length} / ۵۰ کاراکتر`}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SubjectRounded color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                    },
                                }}
                            />

                            <TextField
                                fullWidth
                                multiline
                                minRows={4}
                                maxRows={7}
                                label="شرح درخواست"
                                placeholder="جزئیات درخواست یا مشکل خود را به همراه کد ملی پرسنل یا شناسه فاکتور مربوطه بنویسید..."
                                variant="outlined"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                disabled={submitting}
                                inputProps={{ maxLength: 500 }}
                                helperText={`${message.length} / ۵۰۰ کاراکتر`}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                                            <ChatBubbleOutlineRounded color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                    },
                                }}
                            />

                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2.5,
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(45, 107, 226, 0.05)',
                                    border: '1px solid',
                                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(45, 107, 226, 0.15)',
                                }}
                            >
                                <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                                    💡 <strong>توجه:</strong> پس از ثبت، پاسخ کارشناسان به همراه پیامک اطلاع‌رسانی برای شماره همراه شما ارسال خواهد شد.
                                </Typography>
                            </Box>
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            disabled={submitting}
                            sx={{ borderRadius: 2.5, px: 2.5 }}
                        >
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={submitting || !title.trim() || !message.trim()}
                            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendRounded sx={{ transform: 'rotate(180deg)' }} />}
                            sx={{
                                borderRadius: 2.5,
                                px: 3.5,
                                fontWeight: 700,
                                boxShadow: '0 4px 12px rgba(45, 107, 226, 0.25)',
                            }}
                        >
                            {submitting ? "در حال ثبت تیکت..." : "ارسال تیکت"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </>
    );
};

export default _SupportNew;
